import { useState } from 'react';
import { RegistroCta } from '@/features/auth/components/registro-cta';
import {
  TrendingUp,
  Clock,
  DollarSign,
  Users,
  ArrowRight,
  CheckCircle2,
  Calculator,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DocsLayout } from '../components/docs-layout';

const metrics = [
  {
    value: '85%',
    label: 'Ahorro de Tiempo',
    description: 'Reduccion del tiempo dedicado a crear y gestionar cotizaciones.',
    icon: Clock,
    color: 'from-blue-500 to-cyan-400',
  },
  {
    value: '4.2x',
    label: 'Velocidad vs Manual',
    description: 'Las cotizaciones se crean 4.2 veces mas rapido que con procesos manuales.',
    icon: TrendingUp,
    color: 'from-violet-500 to-purple-400',
  },
  {
    value: '98%',
    label: 'Satisfaccion de Usuarios',
    description: 'Indice de satisfaccion entre empresas que implementaron la plataforma.',
    icon: Users,
    color: 'from-emerald-500 to-green-400',
  },
  {
    value: '12,500+',
    label: 'Cotizaciones Generadas',
    description: 'Total de cotizaciones procesadas en la plataforma.',
    icon: DollarSign,
    color: 'from-orange-500 to-amber-400',
  },
];

const benefitsByRole = [
  {
    role: 'CEO / Director General',
    benefits: [
      'Visibilidad completa del pipeline de cotizaciones y margenes de la empresa.',
      'Reduccion de costos operativos al automatizar procesos manuales.',
      'Datos para tomar decisiones estrategicas de pricing basadas en metricas reales.',
      'Control sobre margenes minimos y politicas de descuento.',
    ],
  },
  {
    role: 'Gerente de Ventas',
    benefits: [
      'Dashboard con desempeno individual de cada vendedor.',
      'Control de aprobaciones con visibilidad de margenes por cotizacion.',
      'Estandarizacion de precios a traves de modelos de pricing reutilizables.',
      'Reduccion del tiempo de coaching con datos de rendimiento automaticos.',
    ],
  },
  {
    role: 'Vendedor',
    benefits: [
      'Crear cotizaciones complejas en minutos en lugar de horas.',
      'Eliminar errores de calculo con formulas automatizadas.',
      'Asistente IA disponible 24/7 para consultas de pricing.',
      'Templates de cotizacion con clonacion rapida.',
    ],
  },
  {
    role: 'Aprobador',
    benefits: [
      'Panel centralizado con todas las cotizaciones pendientes.',
      'Indicadores visuales de margen (verde, amarillo, rojo) para decision rapida.',
      'Flujo de ajuste con precio sugerido para retroalimentar al vendedor.',
      'Estadisticas de tiempo de resolucion y eficiencia de aprobacion.',
    ],
  },
];

const beforeAfter = [
  {
    area: 'Creacion de cotizaciones',
    before: '2-4 horas por cotizacion, usando Excel y templates manuales.',
    after: '15-30 minutos con modelos preconfigurados y calculos automaticos.',
  },
  {
    area: 'Errores de calculo',
    before: 'Frecuentes errores en formulas, margenes mal calculados, precios incorrectos.',
    after: 'Cero errores de calculo con formulas validadas y cascada de costos automatica.',
  },
  {
    area: 'Flujo de aprobacion',
    before: 'Emails, llamadas, WhatsApp. Sin trazabilidad ni historial.',
    after: 'Workflow automatizado con notificaciones, historial y metricas.',
  },
  {
    area: 'Visibilidad de margenes',
    before: 'Se descubre el margen real despues de cerrar la venta.',
    after: 'Indicador de margen en tiempo real durante la creacion de la cotizacion.',
  },
  {
    area: 'Datos y analytics',
    before: 'Reportes manuales en Excel, semanas de atraso.',
    after: 'Dashboards en tiempo real con metricas clave actualizadas automaticamente.',
  },
  {
    area: 'Colaboracion del equipo',
    before: 'Archivos compartidos, versiones multiples, falta de control.',
    after: 'Plataforma centralizada con roles, permisos y flujos definidos.',
  },
];

function RoiCalculator() {
  const [quotesPerMonth, setQuotesPerMonth] = useState(50);
  const [avgTimeHours, setAvgTimeHours] = useState(3);
  const [sellers, setSellers] = useState(5);
  const [hourlyRate, setHourlyRate] = useState(25);

  // 85% time savings
  const currentMonthlyHours = quotesPerMonth * avgTimeHours;
  const savedHours = currentMonthlyHours * 0.85;
  const monthlySavings = savedHours * hourlyRate;
  const annualSavings = monthlySavings * 12;
  const hoursPerSeller = savedHours / (sellers || 1);

  return (
    <Card className="overflow-hidden border-2 border-blue-200 dark:border-blue-800">
      <div className="h-1.5 bg-gradient-to-r from-blue-500 to-violet-500" />
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Calculadora de ROI</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Calcula tu ahorro estimado con Prixia
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {/* Inputs */}
          <div className="space-y-4">
            <div>
              <Label
                htmlFor="quotes"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Cotizaciones por mes
              </Label>
              <Input
                id="quotes"
                type="number"
                min={1}
                value={quotesPerMonth}
                onChange={(e) => setQuotesPerMonth(Number(e.target.value) || 0)}
                className="mt-1"
              />
            </div>
            <div>
              <Label
                htmlFor="time"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Tiempo promedio actual por cotizacion (horas)
              </Label>
              <Input
                id="time"
                type="number"
                min={0.5}
                step={0.5}
                value={avgTimeHours}
                onChange={(e) => setAvgTimeHours(Number(e.target.value) || 0)}
                className="mt-1"
              />
            </div>
            <div>
              <Label
                htmlFor="sellers"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Numero de vendedores
              </Label>
              <Input
                id="sellers"
                type="number"
                min={1}
                value={sellers}
                onChange={(e) => setSellers(Number(e.target.value) || 1)}
                className="mt-1"
              />
            </div>
            <div>
              <Label
                htmlFor="rate"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Costo por hora de vendedor (USD)
              </Label>
              <Input
                id="rate"
                type="number"
                min={1}
                value={hourlyRate}
                onChange={(e) => setHourlyRate(Number(e.target.value) || 0)}
                className="mt-1"
              />
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 border border-blue-100 dark:border-blue-900/50">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Ahorro Mensual Estimado
              </p>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                $
                {monthlySavings.toLocaleString('es-BO', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}{' '}
                USD
              </p>
            </div>

            <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 border border-emerald-100 dark:border-emerald-900/50">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Ahorro Anual Estimado
              </p>
              <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                $
                {annualSavings.toLocaleString('es-BO', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}{' '}
                USD
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
                <p className="text-xs text-gray-500 dark:text-gray-400">Horas ahorradas/mes</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {savedHours.toFixed(0)}h
                </p>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
                <p className="text-xs text-gray-500 dark:text-gray-400">Horas libres/vendedor</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {hoursPerSeller.toFixed(0)}h
                </p>
              </div>
            </div>

            <p className="text-xs text-gray-400 dark:text-gray-500">
              * Basado en un 85% de reduccion de tiempo en la creacion de cotizaciones.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function RoiPage() {
  return (
    <DocsLayout
      title="ROI y Beneficios"
      description="Descubre el impacto real que Prixia puede tener en la productividad y rentabilidad de tu empresa."
    >
      <div className="space-y-12">
        {/* Metricas de valor */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Metricas de Valor
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {metrics.map((metric, idx) => (
              <Card key={idx} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardContent className="p-5 text-center">
                  <div
                    className={`mx-auto w-12 h-12 rounded-xl bg-gradient-to-br ${metric.color} flex items-center justify-center mb-3`}
                  >
                    <metric.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{metric.value}</p>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-1">
                    {metric.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {metric.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* ROI Calculator */}
        <section>
          <RoiCalculator />
        </section>

        {/* Beneficios por rol */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Beneficios por Rol
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {benefitsByRole.map((item, idx) => (
              <Card key={idx}>
                <CardContent className="p-5">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">
                    {item.role}
                  </h3>
                  <ul className="space-y-2">
                    {item.benefits.map((benefit, bIdx) => (
                      <li
                        key={bIdx}
                        className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                      >
                        <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500 shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Antes vs Despues */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Antes vs Despues de Prixia
          </h2>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800">
                      <th className="p-4 text-left font-semibold text-gray-900 dark:text-white">
                        Area
                      </th>
                      <th className="p-4 text-left font-semibold text-red-600 dark:text-red-400">
                        Antes
                      </th>
                      <th className="p-4 text-left font-semibold text-emerald-600 dark:text-emerald-400">
                        Despues
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {beforeAfter.map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-gray-100 dark:border-gray-800/50 last:border-0"
                      >
                        <td className="p-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                          {row.area}
                        </td>
                        <td className="p-4 text-gray-500 dark:text-gray-400 bg-red-50/50 dark:bg-red-950/10">
                          {row.before}
                        </td>
                        <td className="p-4 text-gray-600 dark:text-gray-300 bg-emerald-50/50 dark:bg-emerald-950/10">
                          {row.after}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <div className="text-center py-12 px-6 rounded-2xl bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 border border-blue-100 dark:border-blue-900/50">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Empieza a ahorrar tiempo y dinero hoy
          </h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            Prueba Prixia gratis durante 30 dias. Sin tarjeta de credito, sin compromisos.
          </p>
          <RegistroCta
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-3 rounded-lg hover:shadow-lg transition-shadow"
          >
            Crear Cuenta Gratis
            <ArrowRight className="w-4 h-4" />
          </RegistroCta>
        </div>
      </div>
    </DocsLayout>
  );
}
